<template>
   <div
      class="small-card"
      :class="[
         card?.discipline?.slug ?? route.params.discipline,
         {
            'has-image': card.image,
            'has-learning-goal': card.learning_goal,
            full,
            'no-guest-access': !card.guest_access && isGuest,
         },
      ]"
   >
      <nuxt-link :to="slug">
         <!-- meta -->
         <div class="meta label grey">{{ meta }}</div>
         <div class="flags">
            <!-- if saved -->
            <Tag
               v-if="savable"
               class="unsave action"
               role="button"
               @click.prevent="unsave"
            >
               Unsave
            </Tag>

            <!-- checkmark - unit sequence -->
            <template v-if="showCheck">
               <div class="check">
                  <IconChecked
                     @click.prevent="toggleCompleted"
                     class="checked"
                     v-if="card?.completed"
                  />
                  <IconUnChecked
                     @click.prevent="toggleCompleted"
                     class="unchecked"
                     v-else
                  />
               </div>
            </template>

            <!-- if discipline is coming from card obj -->
            <Tag
               v-if="showDiscipline"
               :class="['discipline', card.discipline.slug]"
            >
               {{ card.discipline.title }}</Tag
            >
            <!-- FLAGS -->
            <Tag v-if="card?.new_flag" class="new-resource"> New </Tag>

            <Tag v-else-if="card?.update_flag" class="updated-resource">
               Updated
            </Tag>
            <Tag v-if="card.guest_access && isGuest" class="guest-resource">
               Guest
            </Tag>
         </div>
         <!-- Core Text -->
         <h4 class="bold">{{ card.title }}</h4>
         <p class="xsmall subtitle" v-if="card.creator">
            {{ card.creator }} - {{ card.piece }}
         </p>

         <!-- LEARNING GOAL TOGGLE -->
         <div
            v-if="card.learning_goal && route.name == 'unit'"
            class="learning-goal-toggle"
            @click.prevent="handleLearningGoalToggle"
         >
            <div class="toggle" :class="{ open: showLearningGoal }">
               <IconShield />
               <span class="label">Learning Goal</span>
               <IconCaret />
            </div>
            <Transition @enter="onEnter" @leave="onLeave">
               <div
                  class="small text"
                  v-html="card.learning_goal"
                  v-if="showLearningGoal"
               ></div>
            </Transition>
         </div>

         <!-- TOPICS -->
         <div class="topics">
            <!-- topics -->
            <p v-if="card.topics" class="xsmall grey">
               {{ card.topics.join(", ") }}
            </p>
            <p v-if="card.resource_type == 'unit-sequence'" class="xsmall grey">
               {{ card.teachables_count }} Teachables
            </p>

            <!-- sort handle -->
            <!-- <div v-if="sortable" class="sort-handle">
            <move />
         </div> -->
         </div>

         <!-- IMAGE -->
         <Image
            v-if="['unit-sequence', 'core-work'].includes(card.resource_type)"
            :image="card.image"
            fit="cover"
         />

         <div class="move" v-if="movable">
            <IconMove />
         </div>
      </nuxt-link>
   </div>
</template>

<script setup lang="ts">
import toastMessages from "~/data/content/toast-messages.json";
import IconShield from "~/assets/images/icons/shield.svg?component";
import IconCaret from "~/assets/images/icons/caret-down.svg?component";
import IconChecked from "~/assets/images/icons/circle-check-filled.svg?component";
import IconUnChecked from "~/assets/images/icons/circle-check.svg?component";
import IconMove from "~/assets/images/icons/move.svg?component";

const { addToast } = useToast();

const { saveTeachableToProfile } = useTeachable();
const { completeClassResource, assignToClass } = useClassesStore();
const route = useRoute();
const gsap = useNuxtApp().$gsap;
const { isGuest } = useAuthStore();

const props = defineProps({
   card: {
      type: Object,
      required: true,
   },
   savable: {
      type: Boolean,
      required: false,
      default: false,
   },
   movable: {
      type: Boolean,
      required: false,
      default: false,
   },
   hideDiscipline: {
      type: Boolean,
      required: false,
      default: false,
   },
   showCheck: {
      type: Boolean,
      required: false,
      default: false,
   },
   full: {
      type: Boolean,
      required: false,
      default: false,
   },
   classId: {
      type: Number,
      required: false,
   },
});
const card = toRef(props, "card");
const showLearningGoal = ref(false);
//>> COMPUTED
const showDiscipline = computed(
   () => card.value?.discipline?.title && !route.name.includes("discipline"),
);
const parentPage = computed(() => {
   return card.value.parentPage;
});

const slug = computed(
   () =>
      routeForResource({
         slug: card.value.slug,
         type:
            parentPage?.value && parentPage.value?.type
               ? `${parentPage.value?.type}-${card.value.type}`
               : (card.value.type ?? card.value.teachable_type),
         parentSlug: parentPage?.value?.slug,
         discipline: card.value.discipline?.slug ?? route.params.discipline,
      }),

   // TODO: REMOVE SLUGBUILDER AND USE { name: routeName, slug: slugName
);

const meta = computed(() => {
   const discipline =
      card.value.discipline?.slug ?? disciplineText(card.value.discipline_id);
   // TODO: Need to standardize this type/teachable_type

   const cardType = card.value?.teachable_type ?? card.value?.type;
   const type = convertType(cardType, discipline);
   const duration = card.value.length ?? null;
   const age = getAges(card.value?.intended_ages ?? null);
   // TODO: Change this be based on discipline
   if (cardType == "unit-sequence" || cardType == "planning-tool") {
      return "Unit Sequence";
   } else if (cardType == "core-work") {
      return "Core Work";
   } else if (cardType == "artistic_insight") {
      return "Artistic Insight";
   }

   if (["practitioner", "tls", "skill"].includes(cardType)) {
      return `${type}`;
   } else {
      return age ? `${type} - ${duration} | ${age}` : `${type} - ${duration}`;
   }
});

const toggleCompleted = async () => {
   const res = await completeClassResource(route.params.slug, {
      teachable_id: card.value?.id,
      completed: !card.value.completed,
   });

   const toastMessage = res.completed
      ? { ...toastMessages.completeClassResource.success }
      : { ...toastMessages.incompleteClassResource.success };
   toastMessage.message = toastMessage.message.replace(/%s/, card.value.title);

   addToast(toastMessage);
};

const unsave = async () => {
   if (route.name == "class") {
      const res = await assignToClass(
         "teachables",
         card.value.slug,
         props.classId,
         "DELETE",
      );

      if (res) {
         const toastMessage = { ...toastMessages.removeFromClass.success };
         toastMessage.message = toastMessage.message.replace(
            /%s/,
            card.value.title,
         );

         addToast(toastMessage);
      }
   } else {
      try {
         await saveTeachableToProfile(card.value.slug, "DELETE");
         const toastMessage = { ...toastMessages.removeFromProfile.success };
         toastMessage.message = toastMessage.message.replace(
            /%s/,
            card.value.title,
         );
         addToast(toastMessage);
      } catch {
         const toastMessage = { ...toastMessages.removeFromProfile.error };
         toastMessage.message = toastMessage.message.replace(
            /%s/,
            card.value.title,
         );
         addToast(toastMessage);
      }
   }
};
//>> METHODS
const handleLearningGoalToggle = () => {
   showLearningGoal.value = !showLearningGoal.value;
};
const onEnter = (el, done) => {
   gsap.to(el, {
      height: "auto",
      ease: "cubic.inOut",
      duration: 0.45,
      onComplete: done,
   });
};
const onLeave = (el, done) => {
   gsap.to(el, {
      height: "0px",
      ease: "cubic.inOut",
      duration: 0.45,
      onComplete: done,
   });
};
</script>

<style lang="scss">
@import "~/assets/scss/globals/small-card";
</style>
